// useStudioPlace — where the author is in the Studio (view state, per
// device; studioLayoutPreference.ts): the page shown (the desk's path), and
// the material the editor works on while the Studio is shown, so every page
// is written on in place (#317; pen "Always writing", one editing mode): a
// document, planned ones too (the author writes on its blank paper, owner
// 30 Oct), unless it is being written (the assistant is writing it; once
// written it loads fresh); a course or module page, which takes free text
// among its parts; and the project's own page when the project is one
// course (that course). A project's own page with no single course, and a
// page about the project (Info, Sources), have nothing to write on. On the
// classic canvas nothing is being edited.
//
// Also where the author is for the assistant (principle 22,
// authorLocation.ts): the page open, the block the caret was last in there
// (the editor reports it as it moves), and the composer's scope chip, which
// follows the open page until the author picks the whole course. All of it
// view state: none survives a reload or means anything to the project.

export type { StudioPlace };
export { openPageOf, studioEdited, useStudioPlace };

import { exhaustSwitchCase } from "@bilda/utils";
import { useState } from "react";
import type { DeskState } from "../../components/Studio/studioDesk.js";
import { docStateOf } from "../../components/StudioDesk/deskFacts.js";
import {
    type AuthorCaret,
    type AuthorLocation,
    authorLocationOf,
} from "../authorLocation.js";
import type { StudioSurface } from "../frame.js";
import type { Library } from "../ports.js";
import { readStudioDesk } from "../studioLayoutPreference.js";

type StudioPlace = {
    readonly desk: DeskState;
    readonly setDesk: (desk: DeskState) => void;
    // The material the editor works on (undefined: none).
    readonly edited: string | undefined;
    // Where the author is, as the next message reports it (undefined: no
    // page open).
    readonly location: AuthorLocation | undefined;
    // The editor's caret moved to a block of the edited page, with the text
    // selected there (absent: none).
    readonly noteCaret: (
        blockId: string,
        selection: string | undefined,
    ) => void;
    // The scope chip: the whole course or project, or back to the open page.
    readonly scopeToWhole: () => void;
    readonly scopeToPage: () => void;
};

// The project's one course, the material its own page shows (undefined:
// none, or more than one). The courses on its canvas not drawn inside
// another container there (studioCards.ts, `rootOf`).
const projectCourse = (
    library: Library | null,
    project: string | null,
): string | undefined => {
    const canvas = new Set(
        (library?.projects ?? [])
            .find((p) => p.id === project)
            ?.canvas.map((i) => i.target) ?? [],
    );
    const courses = (library?.materials ?? []).filter(
        (m) =>
            canvas.has(m.id) &&
            m.container?.kind === "course" &&
            !(m.partOf !== undefined && canvas.has(m.partOf.rootId)),
    );
    const [only, ...more] = courses;
    return more.length === 0 ? only?.id : undefined;
};

// Pure: what the editor works on for the page shown.
const studioEdited = (
    desk: DeskState,
    library: Library | null,
    project: string | null,
): string | undefined => {
    const shown = desk.path.at(-1);
    if (shown === undefined) return projectCourse(library, project);
    switch (shown.kind) {
        case "document": {
            const item = library?.materials.find((m) => m.id === shown.id);
            const state = docStateOf({
                kind: "material",
                planned: item?.planned === true,
                document: item?.document,
            });
            return state === "writing" ? undefined : shown.id;
        }
        case "binder":
        case "shelf":
            return shown.id;
        case "info":
        case "objectives":
        case "sources":
            return undefined;
        default:
            return exhaustSwitchCase(shown.kind);
    }
};

// Pure: the page open on the desk, as the assistant should read "this page":
// a document (being written too), a module or course page; the project's own
// page is its one course (`edited`). A page about the project is none.
const openPageOf = (
    desk: DeskState,
    edited: string | undefined,
): string | undefined => {
    const shown = desk.path.at(-1);
    if (shown === undefined) return edited;
    switch (shown.kind) {
        case "document":
        case "binder":
        case "shelf":
            return shown.id;
        case "info":
        case "objectives":
        case "sources":
            return undefined;
        default:
            return exhaustSwitchCase(shown.kind);
    }
};

const useStudioPlace = (
    library: Library | null,
    surface: StudioSurface,
    project: string | null,
): StudioPlace => {
    const [desk, setDesk] = useState(readStudioDesk);
    // View state: the caret's last block, and the page the author widened
    // the scope from (the chip follows the next page opened).
    const [caret, setCaret] = useState<AuthorCaret | undefined>(undefined);
    const [wholeFrom, setWholeFrom] = useState<string | undefined>(undefined);
    const edited =
        surface === "pages" ? studioEdited(desk, library, project) : undefined;
    const page =
        surface === "pages" && project !== null
            ? openPageOf(desk, edited)
            : undefined;
    // The project's own page is the whole already.
    const whole = desk.path.length === 0 || wholeFrom === page;
    return {
        desk,
        setDesk,
        edited,
        location: authorLocationOf(
            page,
            whole ? "whole_project" : "open_page",
            caret,
        ),
        // Typing moves the caret within a block: the same place keeps the
        // same value, so nothing re-renders.
        noteCaret: (blockId, selection) => {
            if (edited === undefined) return;
            setCaret((prev) =>
                prev?.pageId === edited &&
                prev.blockId === blockId &&
                prev.selection === selection
                    ? prev
                    : {
                          pageId: edited,
                          blockId,
                          ...(selection === undefined ? {} : { selection }),
                      },
            );
        },
        scopeToWhole: () => setWholeFrom(page),
        scopeToPage: () => setWholeFrom(undefined),
    };
};
